import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Globe, BarChart3, Shield, Settings, Building2 } from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const Section = ({ icon: Icon, title, children }) => (
  <section>
    <h2 className="text-xl md:text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5 text-[#1e3a5f] shrink-0" />
      {title}
    </h2>
    {children}
  </section>
);

const CookiesPolicy = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cookies-policy-page">
      <SEO title="Cookies Policy | StayMii" description="How StayMii uses cookies and tracking technologies for session management, analytics, and user experience." canonicalUrl="/cookies" />
      <LandingNavBar />

      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] pt-28 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Cookie className="h-10 w-10 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="cookies-title">Cookies Policy</h1>
          <p className="text-white/70 text-sm">Last updated: March 18, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8 space-y-8">

          <section>
            <p className="text-slate-600 leading-relaxed">
              This Cookies Policy explains how StayMii, operated by Mii Global Partner Holdings Sdn Bhd (Registration No: 202601028203), uses cookies and similar tracking technologies on our website and platform. By continuing to use StayMii, you consent to the use of cookies as described in this policy.
            </p>
          </section>

          <Section icon={Globe} title="1. What Are Cookies?">
            <p className="text-slate-600 leading-relaxed">
              Cookies are small text files placed on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work efficiently, improve user experience, and provide information to website operators.
            </p>
          </Section>

          <Section icon={Cookie} title="2. Types of Cookies We Use">
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">Essential Cookies</h3>
                <p className="text-slate-600 text-sm mb-2">These cookies are necessary for the basic functionality of the StayMii platform. They enable:</p>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1 ml-2">
                  <li>User authentication and session management</li>
                  <li>Security features and access control</li>
                  <li>System preferences and settings</li>
                </ul>
                <p className="text-slate-500 text-xs mt-2">These cookies cannot be disabled as they are required for the platform to function.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">Performance & Analytics Cookies</h3>
                <p className="text-slate-600 text-sm mb-2">These cookies help us understand how users interact with StayMii, including:</p>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1 ml-2">
                  <li>Page visit frequency and navigation patterns</li>
                  <li>Error tracking and performance monitoring</li>
                  <li>Feature usage analytics</li>
                </ul>
                <p className="text-slate-500 text-xs mt-2">Data collected through analytics cookies is aggregated and anonymous.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">Functional Cookies</h3>
                <p className="text-slate-600 text-sm mb-2">These cookies remember your preferences and settings, such as:</p>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1 ml-2">
                  <li>Language and display preferences</li>
                  <li>Dashboard layout and widget configurations</li>
                  <li>Recent search and filter selections</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">Third-Party Cookies</h3>
                <p className="text-slate-600 text-sm">StayMii may use third-party services that set their own cookies, such as analytics providers, payment processors, and OTA integration partners for StayMii e-Ticket. These third parties have their own cookie and privacy policies.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">StayMii e-Ticket Buyer Session Cookies</h3>
                <p className="text-slate-600 text-sm">When a ticket buyer purchases a ticket through a StayMii e-Ticket booking page, a short-lived session cookie may be set to preserve their cart, selected time slot, and payment state until checkout completes. These cookies are essential to complete the purchase and are cleared once the transaction ends.</p>
              </div>
            </div>
          </Section>

          <Section icon={BarChart3} title="3. How We Use Cookies">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii uses cookies for the following purposes:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Authenticating users and maintaining secure login sessions</li>
              <li>Remembering user preferences and platform settings</li>
              <li>Analyzing website traffic and usage patterns to improve performance</li>
              <li>Ensuring platform security and detecting suspicious activity</li>
              <li>Supporting integrations with third-party hospitality systems</li>
            </ul>
          </Section>

          <Section icon={Shield} title="4. Cookie Duration">
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-1">Session Cookies</h3>
                <p className="text-slate-600 text-sm">Temporary cookies that are deleted when you close your browser. Used for session management and authentication.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-1">Persistent Cookies</h3>
                <p className="text-slate-600 text-sm">Cookies that remain on your device for a set period or until manually deleted. Used for remembering preferences and login details.</p>
              </div>
            </div>
          </Section>

          <Section icon={Settings} title="5. Managing Cookies">
            <p className="text-slate-600 leading-relaxed mb-3">You can control and manage cookies through your browser settings. Most browsers allow you to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>View and delete existing cookies</li>
              <li>Block all or specific cookies</li>
              <li>Set preferences for certain websites</li>
              <li>Receive notifications when cookies are set</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
              <p className="text-amber-800 text-sm">Please note that disabling essential cookies may affect the functionality of the StayMii platform, including login and session management features.</p>
            </div>
          </Section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">6. Updates to This Policy</h2>
            <p className="text-slate-600 leading-relaxed">StayMii may update this Cookies Policy from time to time. Updated versions will be published on the StayMii website. Continued use of the platform indicates acceptance of the revised policy.</p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">7. Related Policies</h2>
            <p className="text-slate-600 leading-relaxed mb-3">For more information about how we handle your data, please refer to:</p>
            <div className="space-y-2">
              <Link to="/privacy-policy" className="block bg-slate-50 rounded-lg p-3 text-[#2563EB] text-sm font-medium hover:bg-blue-50 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="block bg-slate-50 rounded-lg p-3 text-[#2563EB] text-sm font-medium hover:bg-blue-50 transition-colors">Terms of Service</Link>
            </div>
          </section>

          <Section icon={Building2} title="8. Contact Information">
            <p className="text-slate-600 leading-relaxed mb-3">For questions regarding this Cookies Policy:</p>
            <div className="bg-slate-50 rounded-lg p-4 text-slate-600 text-sm">
              <p className="font-semibold text-slate-800">Mii Global Partner Holdings Sdn Bhd</p>
              <p>Registration No: 202601028203</p>
              <p>11, Taman Bukit Dumbar, 11600 Jelutong, Penang, Malaysia</p>
              <p className="mt-2">Email: <a href="mailto:support@staymii.com" className="text-[#2563EB] hover:underline">support@staymii.com</a></p>
              <p>Website: <a href="https://staymii.com" className="text-[#2563EB] hover:underline">https://staymii.com</a></p>
            </div>
          </Section>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default CookiesPolicy;
